import { Link, useNavigate } from "react-router-dom";
import { Bell } from "lucide-react";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";
import { api, errMsg } from "../lib/api";
import { useApp, useData } from "../lib/app";
import LoadRing from "../components/LoadRing";
import TaskCard from "../components/TaskCard";
import { AiLoader, Empty, ErrorCard } from "../components/bits";
import { AajiButton } from "./Family";
import { useT } from "../lib/i18n";

const greet = (h) => (h < 12 ? "Good morning" : h < 17 ? "Good afternoon" : "Good evening");

export default function HomePage() {
  const { me } = useApp();
  const nav = useNavigate();
  const qc = useQueryClient();
  const t = useT();
  const { data: s, isLoading, error, refetch } = useData("/summary", { refetchInterval: 30000 });
  if (isLoading) return <div className="pt-16"><AiLoader kind="story" /></div>;
  if (error) return <div className="pt-16"><ErrorCard onRetry={refetch} /></div>;
  const toggleMilk = async () => {
    try { const { data } = await api.post("/milk/nomilk"); toast(t(data.no_milk_today ? "Logged: no milk today 🥛" : "Undone")); qc.invalidateQueries({ queryKey: ["/summary"] }); } catch (e) { toast.error(errMsg(e)); }
  };
  const tiles = [["speak", "🎤", "Speak"], ["scan", "📷", "Scan"], ["type", "⌨️", "Type / paste"]];
  return (
    <div className="relative overflow-x-clip">
      <div className="ul-blob w-56 h-56 -top-10 -right-16" style={{ background: "#FFD1E3" }} />
      <div className="ul-blob w-48 h-48 top-8 -left-20" style={{ background: "#D9D2FF" }} />
      <div className="relative flex items-start justify-between pt-5 pb-4">
        <div>
          <p className="text-[#6B6680] font-semibold" data-testid="home-greeting">{t(greet(s.hour))}, {me.me.name}</p>
          <h1 className="font-display text-[30px] font-extrabold leading-tight">{me.home.name}</h1>
        </div>
        <Link to="/notifications" data-testid="home-bell" className="relative w-12 h-12 rounded-full bg-white ul-card flex items-center justify-center">
          <Bell size={22} className="text-[#6A5AE0]" />
          {s.unread > 0 && <span data-testid="home-unread-badge" className="absolute -top-1 -right-1 min-w-[22px] h-[22px] px-1 rounded-full bg-[#FF6FA3] text-white text-xs font-extrabold flex items-center justify-center">{s.unread}</span>}
        </Link>
      </div>
      <div className="relative grid grid-cols-3 gap-3 mb-4">
        {tiles.map(([k, e, tt]) => (
          <button key={k} data-testid={`home-tile-${k}`} onClick={() => nav(`/unload/${k}`)} className="ul-card py-4 flex flex-col items-center gap-1 hover:-translate-y-0.5 transition-transform">
            <span className="text-3xl">{e}</span><span className="font-bold text-sm">{t(tt)}</span>
          </button>
        ))}
      </div>
      {me.admin && !s.setup_done && (
        <button data-testid="home-finish-setup" onClick={() => nav("/setup")} className="ul-card w-full p-4 mb-4 text-left" style={{ background: "#F3F0FF" }}>
          <b>{t("✨ Finish your home setup")}</b><span className="block text-sm text-[#6B6680]">{t("2 minutes, and the app plans repeating reminders for you.")}</span>
        </button>
      )}
      <LoadRing week={s.week} meId={me.me.id} />
      {s.hour >= 19 && !s.checkin_today && (
        <button data-testid="home-checkin-card" onClick={() => nav("/checkin")} className="ul-card w-full p-4 mt-4 text-left" style={{ background: "linear-gradient(120deg,#F3F0FF,#FFF0F6)" }}>
          <b className="font-display text-lg">{t("🌙 How's your mind tonight?")}</b><span className="block text-sm text-[#6B6680]">{t("😌 Calm · 🙂 Okay · 😩 Overloaded · 🔒 only you can see it")}</span>
        </button>
      )}
      <div className="flex items-center justify-between mt-6 mb-2">
        <p className="ul-label">{t("Today, by person")}</p>
        {s.has_milk && (
          <button data-testid="no-milk-btn" onClick={toggleMilk} className="ul-chip" data-on={s.no_milk_today}>{t(s.no_milk_today ? "🥛 No milk today ✓" : "🥛 No milk today")}</button>
        )}
      </div>
      <div className="space-y-3" data-testid="home-today-list">
        {s.today.length === 0 ? <Empty emoji="🌸" text="Nothing on your mind right now. Lovely. 🌸" /> : s.today.map((tk) => <TaskCard key={tk.id} t={tk} compact />)}
      </div>
      {s.recent && <p className="mt-4 ul-card p-3 text-sm font-semibold" data-testid="home-recent">🙏 {s.recent}</p>}
      <div className="grid grid-cols-2 gap-3 mt-4">
        <button data-testid="home-story-card" onClick={() => nav("/story")} className="ul-card p-4 text-left" style={{ background: "linear-gradient(135deg,#6A5AE0,#FF6FA3)", color: "#fff" }}>
          <b className="font-display text-lg">{t("📖 Your week's story")}</b><span className="block text-xs opacity-90">{t("{n} things this week", { n: s.week.total })}</span>
        </button>
        <button data-testid="home-checkin-open" onClick={() => nav("/checkin")} className="ul-card p-4 text-left">
          <b className="font-display text-lg">{t("💜 Check-in")}</b><span className="block text-xs text-[#6B6680]">{s.checkin_today ? t("Today: {m}", { m: t(s.checkin_today) }) : t("How's your mind?")}</span>
        </button>
      </div>
      {me.home.demo && me.admin && <div className="mt-4"><AajiButton /></div>}
    </div>
  );
}
